import { Box, Skeleton, Slide, useTheme } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { isSameOrNestedPath, portalPaths } from '@provision/portal-routes';
import { type KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { EllipsesMenu, FilterChips, InboxClearIllustration } from 'src/components';
import { NotificationStatus, useNotificationRecords } from 'src/graphql/entities/notification';
import {
  useInViewTrigger,
  useScrollFallbackWhenNotOverflowing,
  useScrollPaginateHandler,
} from 'src/utils/hooks';
import { getNextListIndex } from 'src/utils/hooks/use-list-keyboard-navigation';
import { useNavigateRoute } from 'src/utils/routing';
import { useInboxArchive } from './hooks/use-inbox-archive';
import { useInboxFilters } from './hooks/use-inbox-filters';
import { useInboxMenuItems } from './hooks/use-inbox-menu-items';
import { useNotificationMenuItems } from './hooks/use-notification-menu-items';
import { useNotificationNavigation } from './hooks/use-notification-navigation';
import { InboxPaneItem } from './inbox-pane-item';
import {
  ARCHIVE_EXIT_TIMEOUT_MS,
  INBOX_FILTER_OPERATOR_DISPLAY,
  INBOX_PANE_ALL_CLEAR_TEST_ID,
  INBOX_PANE_CLEAR_FILTER_TEST_ID,
  INBOX_PANE_ITEM_TEST_ID,
  INBOX_PANE_NO_MATCH_TEST_ID,
} from './inbox-pane.constants';
import {
  AllClearState,
  ChipsRow,
  ClearFilterButton,
  getHeadToolSx,
  Header,
  ItemTransitionWrapper,
  NoMatchCaption,
  NoMatchIcon,
  NoMatchState,
  ScrollArea,
  SkeletonRow,
} from './inbox-pane.styles';

const INBOX_ROOT_PATH = portalPaths.inbox.root();

const DEFAULT_FILTERS = [
  { attribute: 'status', operator: '!=', value: NotificationStatus.Archived },
];

const ArchiveNotificationTransition = (
  props: TransitionProps & { children: React.ReactElement },
) => {
  const { children, in: inProp, ...transitionProps } = props;

  return (
    <ItemTransitionWrapper
      {...transitionProps}
      appear={false}
      enter={false}
      in={inProp}
      timeout={ARCHIVE_EXIT_TIMEOUT_MS}
    >
      <Slide
        appear={false}
        direction="right"
        enter={false}
        in={inProp}
        timeout={ARCHIVE_EXIT_TIMEOUT_MS}
        mountOnEnter
        unmountOnExit
      >
        <Box>{children}</Box>
      </Slide>
    </ItemTransitionWrapper>
  );
};

export const InboxPane = () => {
  const theme = useTheme();

  const {
    filters,
    chipConditions,
    chipQueryOptions,
    handleChipConditionsChange,
    deleteChipCondition,
    filterMenuOptions,
  } = useInboxFilters();

  const { notifications, loadMore, hasMore, isLoading, isLoadingMore } =
    useNotificationRecords({
      filters: [...DEFAULT_FILTERS, ...filters],
    });

  const { menuItems, markOneAsRead } = useInboxMenuItems();

  const { navigateToTarget, handleItemClick, openTargetDebounced, isRowSelected } =
    useNotificationNavigation({
      markOneAsRead,
    });

  const { activeIndex, setActiveIndex, archiveAt, openNextAfterIfSelected } = useInboxArchive({
    notifications,
    isRowSelected,
    navigateToTarget,
  });

  const { getMenuItems } = useNotificationMenuItems();

  const isEmpty = !isLoading && notifications.length === 0;
  const isFilterActive = filters.length > 0;
  // the celebration is for true zero; a Type filter matching nothing is not it
  const isAllClear = isEmpty && !isFilterActive;

  const { navigate, pathnameWithoutPrefix } = useNavigateRoute();
  // only the bare /inbox route puts the clear-state page in the centre; anywhere else
  // (the grid the pane usually sits over) nothing would mark the moment, so the pane
  // takes the illustration itself rather than showing it twice side by side
  const isCelebratedInCentre = pathnameWithoutPrefix.replace(/\/$/, '') === INBOX_ROOT_PATH;
  // a preview left open after the inbox emptied is a dead end; hand it to the
  // clear-state page. Transition only, so a deep link into an already-archived
  // notification still shows its record
  const hadNotificationsRef = useRef(false);
  useEffect(() => {
    if (notifications.length > 0) {
      hadNotificationsRef.current = true;
      return;
    }
    if (!hadNotificationsRef.current || !isAllClear) return;
    hadNotificationsRef.current = false;

    if (isSameOrNestedPath(pathnameWithoutPrefix, INBOX_ROOT_PATH) && !isCelebratedInCentre) {
      navigate(INBOX_ROOT_PATH, { replace: true });
    }
  }, [
    notifications.length,
    isAllClear,
    pathnameWithoutPrefix,
    isCelebratedInCentre,
    navigate,
  ]);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  // keyboard scrolling slides rows under the stationary cursor, firing their
  // mouseenter — which would yank the active row back to the mouse position.
  // Suppress hover updates until the mouse physically moves again.
  const hoverSuppressedRef = useRef(false);

  useEffect(() => {
    scrollAreaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (activeIndex === null || !scrollAreaRef.current) return;

    const rows = scrollAreaRef.current.querySelectorAll<HTMLElement>(
      `[data-testid="${INBOX_PANE_ITEM_TEST_ID}"]`,
    );
    const row = rows[activeIndex];
    if (typeof row?.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const targetEl = event.target as HTMLElement;
      if (
        targetEl.tagName === 'INPUT' ||
        targetEl.tagName === 'TEXTAREA' ||
        targetEl.isContentEditable
      ) {
        return;
      }

      event.preventDefault();
      hoverSuppressedRef.current = true;

      const nextIndex = getNextListIndex({
        key: event.key,
        currentIndex: activeIndex ?? -1,
        itemCount: notifications.length,
        wrap: false,
      });
      if (nextIndex === null || nextIndex < 0) return;

      setActiveIndex(nextIndex);
      openTargetDebounced(notifications[nextIndex]);
    },
    [activeIndex, notifications, openTargetDebounced, setActiveIndex],
  );

  const handleScroll = useScrollPaginateHandler({
    loadMore,
    enabled: hasMore,
    thresholdPx: 300,
  });

  useScrollFallbackWhenNotOverflowing({
    containerRef: scrollAreaRef,
    onScroll: hasMore ? handleScroll : undefined,
    dataKey: notifications.length,
  });

  const inViewRef = useInViewTrigger<HTMLDivElement>({
    onEnter: () => loadMore(),
    enabled: !isLoading && hasMore && !isLoadingMore,
  });

  return (
    <>
      <Header>
        {filterMenuOptions?.length ? (
          <V2ThemeProvider>
            <EllipsesMenu
              options={filterMenuOptions}
              icon="filter"
              iconLibrary="lucide"
              title="Filter"
              triggerTestId="inbox-pane-filter"
              sx={getHeadToolSx(theme)}
            />
          </V2ThemeProvider>
        ) : null}
        {menuItems?.length ? (
          <V2ThemeProvider>
            <EllipsesMenu
              options={menuItems}
              icon="more-vertical"
              iconLibrary="lucide"
              title="More"
              triggerTestId="inbox-pane-more"
              sx={getHeadToolSx(theme)}
            />
          </V2ThemeProvider>
        ) : null}
      </Header>

      {chipConditions?.length > 0 && (
        <ChipsRow>
          <FilterChips
            autoValueSelector
            conditions={chipConditions}
            onConditionsChange={handleChipConditionsChange}
            deleteCondition={deleteChipCondition}
            queryOptions={chipQueryOptions}
            operatorDisplayOverrides={INBOX_FILTER_OPERATOR_DISPLAY}
          />
        </ChipsRow>
      )}

      <ScrollArea
        ref={scrollAreaRef}
        onScroll={handleScroll}
        onKeyDown={handleListKeyDown}
        onMouseMove={() => {
          hoverSuppressedRef.current = false;
        }}
        tabIndex={-1}
        data-testid="inbox-pane-scroll-area"
      >
        {!!isLoading &&
          // loading items should use indexes key
          Array.from({ length: 15 }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={idx}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="90%" height={14} />
              </Box>
              <Skeleton variant="text" width={28} height={14} />
            </SkeletonRow>
          ))}

        {!!isAllClear && (
          <AllClearState data-testid={INBOX_PANE_ALL_CLEAR_TEST_ID}>
            {!isCelebratedInCentre && <InboxClearIllustration size="pane" />}
            Inbox all clear
          </AllClearState>
        )}

        {!!isEmpty && !!isFilterActive && (
          <NoMatchState data-testid={INBOX_PANE_NO_MATCH_TEST_ID}>
            <NoMatchIcon name="search-x" library="lucide" size={34} strokeWidth={1.4} />
            <NoMatchCaption>No notifications match this filter.</NoMatchCaption>
            <ClearFilterButton
              onClick={() => handleChipConditionsChange([])}
              data-testid={INBOX_PANE_CLEAR_FILTER_TEST_ID}
            >
              Clear filter
            </ClearFilterButton>
          </NoMatchState>
        )}

        <TransitionGroup component={null}>
          {notifications.map((n, idx) => (
            <ArchiveNotificationTransition key={n.id}>
              <InboxPaneItem
                notification={n}
                isSelected={isRowSelected(n)}
                isActive={activeIndex === idx}
                menuItems={getMenuItems(n, { onArchived: () => openNextAfterIfSelected(n) })}
                onClick={(event) =>
                  handleItemClick(n, {
                    shiftKey: event.shiftKey,
                    metaKey: event.metaKey,
                    ctrlKey: event.ctrlKey,
                  })
                }
                onMouseEnter={() => {
                  if (!hoverSuppressedRef.current) setActiveIndex(idx);
                }}
                onMouseLeave={() => {
                  if (!hoverSuppressedRef.current) setActiveIndex(null);
                }}
                onSwipeArchive={() => archiveAt(idx)}
              />
            </ArchiveNotificationTransition>
          ))}
        </TransitionGroup>

        {!!isLoadingMore &&
          !!hasMore &&
          // loading items should use indexes key
          Array.from({ length: 10 }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <SkeletonRow key={idx}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="90%" height={14} />
              </Box>
              <Skeleton variant="text" width={28} height={14} />
            </SkeletonRow>
          ))}

        {/* ensures progress when first pages collapse to few items after grouping */}
        <Box ref={inViewRef} sx={{ height: '1px' }} />
      </ScrollArea>
    </>
  );
};
